import { usePostApi } from '@/apiError/apiCreate';
import { useUpdateApi } from '@/apiError/apiUpdate';
import { AntButton } from '@/common/ant-components/AntButton';
import {
  rotateTabWithinForm,
  useInputRef,
} from '@/utils/validation/FormValidation';
import { Checkbox, Col, Form, Input, Row, Select, Space } from 'antd';
import { useEffect, useState } from 'react';
import InventoryGroupFormSkeleton from './InventoryGroupFormSkeleton';
import { ShowAllData, useMutateGetApi } from '@/apiError/apiShow';
import { FormInstance } from 'antd';
import { useNavigate } from 'react-router-dom';
import { showToast } from '@/utils/toastMessage/ToastMessage';

interface Props {
  referenceId: string;
  onClose: () => void;
  refreshTable: () => void;
  groupId?: string;
  form: FormInstance;
}

interface FormValue {
  name: string;
  groupId: String;
  isGroupLinked: Boolean;
}

interface GroupListType {
  name: String;
  referenceId: String;
}

/**
 * InventoryGroupForm Component
 * Handles both creation and editing of inventory groups.
 * Props:
 * - mode: "create" | "edit" (Determines the behavior of the form)
 */
const InventorySubGroupForm = ({
  referenceId,
  onClose = () => {},
  refreshTable = () => {},
  groupId,
  form: propForm,
}: Props) => {
  const { firstInputRef, lastInputRef } = useInputRef();
  const [form] = propForm ? [propForm] : Form.useForm();
  const [groupList, setGrouplist] = useState([]);
  const [isGroup, setIsGroup] = useState(false);
  const [isName, setIsName] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { mutateAsync: createItem, isPending: createLoading } = usePostApi({
    url: 'account/inventory/sub-groups/',
    onSuccess: () => {},
  });
  const { mutateAsync: updateData, isPending: updateLoading } = useUpdateApi();
  const { mutateAsync: getDataById, isPending: showLoading } =
    useMutateGetApi();
  /**
   * Handles the submission of the form for both creation and updating.
   * Sends an API request based on the mode.
   */
  const handleSubmit = async (formValues: FormValue) => {
    if (formValues.isGroupLinked && !formValues.groupId) {
      showToast('Please Select a Group', 'error');
      return;
    }
    try {
      // setLoading(true);
      if (referenceId) {
        //@ts-ignore
        await updateData({
          url: `account/inventory/sub-groups/${referenceId}/`,
          values: formValues,
        });
      } else {
        await createItem(formValues);
      }
      onClose();
      form.resetFields();
      refreshTable();
    } finally {
      // setLoading(false);
    }
  };

  const onChange = async (e: any) => {
    setIsGroup(e.target.checked);
    if (e.target.checked) {
      setIsName(false);
    } else {
      setIsName(true);
      form.setFieldValue('groupId', null);
    }
  };

  const groupSelectHandler = (value: String) => {
    setIsName(true);
  };
  /**
   * Fetches the list of inventory groups to populate the dropdown
   */
  useEffect(() => {
    const fetchGroupList = async () => {
      const response: any = await ShowAllData.showDetails(
        'account/inventory/groups/',
        navigate,
      );
      setGrouplist(
        response?.data?.map((item: GroupListType) => ({
          label: item.name,
          value: item.referenceId,
        })),
      );
    };
    fetchGroupList();
  }, []);

  /**
   * Fetches data for an existing inventory group in edit mode.
   */
  useEffect(() => {
    if (referenceId) {
      const fetchInventoryGroupData = async () => {
        try {
          setLoading(true);
          //@ts-ignore
          const response = await getDataById({
            url: `account/inventory/sub-groups/${referenceId}/`,
          });

          const list: any = await ShowAllData.showDetails(
            'account/inventory/groups/',
            navigate,
          );
          setGrouplist(
            list?.data?.map((item: GroupListType) => ({
              label: item.name,
              value: item.referenceId,
            })),
          );
          setIsGroup(true);
          form.setFieldsValue({
            name: response?.data?.name,
            groupId: response?.data?.groupId,
            isGroupLinked: response?.data?.groupId ? true : false,
          });
        } finally {
          setLoading(false);
        }
      };
      fetchInventoryGroupData();
    }
    if (groupId) {
      form.setFieldValue('groupId', groupId);
      form.setFieldValue('isGroupLinked', true);
    }
  }, [referenceId]);

  const handleFormKeyDown = (e: any) => {
    rotateTabWithinForm(e, firstInputRef, lastInputRef);
  };

  return showLoading || loading ? (
    <InventoryGroupFormSkeleton />
  ) : (
    <>
      {/* <LoadingOverlay loading={loading} /> */}
      <Form
        form={form}
        initialValues={{ name: '', groupId: null, isGroupLinked: false }}
        layout="vertical"
        onFinish={handleSubmit}
        onKeyDown={handleFormKeyDown}
      >
        <Row gutter={24} style={{ marginBottom: '16px' }}>
          {/* <Col xs={24} md={12} lg={4}>
            <Form.Item
              name="isGroupLinked"
              className="username"
              valuePropName="checked"
            >
              <Checkbox ref={firstInputRef} onChange={onChange}>
                Is Sub Group
              </Checkbox>
            </Form.Item>
          </Col> */}
          {/* <Col xs={24} md={12} lg={8}>
            <Form.Item name="groupId" label="Group Name" className="username">
              <Select
                placeholder="Select group"
                options={groupList}
                loading={loading}
                onChange={groupSelectHandler}
                disabled={!isGroup}
              />
            </Form.Item>
          </Col> */}
          <Col xs={24}>
            <Form.Item
              name="name"
              label="Category Name"
              className="username"
              rules={[
                {
                  required: true,
                  message: 'Please enter the category name!',
                },
              ]}
            >
              <Input disabled={!isName} type="text" />
            </Form.Item>
          </Col>
        </Row>
        <div style={{ textAlign: 'right' }}>
          <Space size="large">
            <AntButton
              color="green"
              type="primary"
              htmlType="submit"
              loading={createLoading || updateLoading}
            >
              {referenceId ? 'Update' : 'Create'}
            </AntButton>
            <AntButton color="red" onClick={onClose} ref={lastInputRef}>
              Cancel
            </AntButton>
          </Space>
        </div>
      </Form>
    </>
  );
};

export default InventorySubGroupForm;
